// Media URL helper — routes cross-origin external URLs (fal.ai CDN, etc.)
// through the app's `/api/proxy/media` streaming proxy so the browser treats
// the response as same-origin. This is required for `<video crossOrigin>`
// canvas capture (Editor's Capture Frame flow) to avoid tainted-canvas
// SecurityError on fal-hosted content.
//
// Same-origin URLs (already served from our backend, including /api/files/*)
// are returned unchanged since they're CORS-safe after our backend fix that
// sets Access-Control-Allow-Origin: * on /api/files responses.
//
// Any host that our backend accepts in _MEDIA_PROXY_HOST_ALLOWLIST is
// proxied. Anything else is returned unchanged (either same-origin, or a
// data:/blob:/file:/relative URL that never needed the proxy anyway).

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");

// Mirror the backend allow-list — keep these in sync with
// backend/server.py::_MEDIA_PROXY_HOST_ALLOWLIST.
const PROXY_HOSTS = [
  "fal.media",
  "v2.fal.media",
  "v3.fal.media",
  "fal.ai",
  "cdn.fal.ai",
  "storage.googleapis.com",
  "storage.emergentagent.com",
];

const isProxyableHost = (hostname) => {
  const h = (hostname || "").toLowerCase();
  return PROXY_HOSTS.some((allowed) => h === allowed || h.endsWith("." + allowed));
};

/**
 * Wrap an external media URL so it loads through the app's CORS-safe proxy.
 * If the URL is same-origin, empty, a data: / blob: URL, or points at a host
 * outside the proxy allow-list, it's returned unchanged.
 *
 * @param {string} url — the source media URL
 * @returns {string} — either the original URL or a `/api/proxy/media?url=…`
 *                     variant that will be served through our backend.
 */
export const proxyMediaUrl = (url) => {
  if (!url || typeof url !== "string") return url;
  // Fast-path: data / blob / relative URLs — never need proxying.
  if (/^(data:|blob:|\/)/i.test(url)) return url;

  let parsed;
  try { parsed = new URL(url); }
  catch { return url; }

  // Same-origin — served by our own backend (which now has proper CORS
  // headers on /api/files/*). Passthrough.
  try {
    if (parsed.origin === window.location.origin) return url;
  } catch (_) { /* SSR safety */ }

  // Only wrap through the proxy for hosts the backend actually accepts —
  // otherwise the proxy will just 400 and we'd break random third-party
  // assets (avatar CDNs, docs, …) that never needed CORS anyway.
  if (!isProxyableHost(parsed.hostname)) return url;

  if (!BACKEND_URL) return url;   // no backend configured (shouldn't happen)

  return `${BACKEND_URL}/api/proxy/media?url=${encodeURIComponent(url)}`;
};
